import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { SignupPath } from '@/modules/auth/paths';
import { HomePath } from '@/routing/paths';
import { getQueryParamCredentials } from '@/utils/url/getQueryParamCredentials';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const EncodedCredentialsQueryParamName = 'c';
const CredentialQueryParamNames = {
  username: ['u', 'username'],
  password: ['p', 'password'],
};

const Login = () => {
  const auth = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { username: usernameQueryParam, password: passwordQueryParam } = getQueryParamCredentials(
    searchParams,
    EncodedCredentialsQueryParamName,
    CredentialQueryParamNames,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState(usernameQueryParam);
  const [password, setPassword] = useState(passwordQueryParam);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(
    location.state?.sessionExpired ? 'Your session has expired. Please sign in again.' : null,
  );
  const navigate = useNavigate();
  const hasAttemptedQueryParamLogin = useRef(false);

  const handleLogin = useCallback(
    async (loginUsername = username, loginPassword = password) => {
      if (!loginUsername || !loginPassword) {
        setError('Please enter both username and password.');
        return;
      }

      setError(null);
      setIsLoading(true);
      const isLoggedIn = await auth.login(loginUsername, loginPassword);
      setIsLoading(false);

      if (!isLoggedIn) {
        setError('Incorrect username or password. Please try again.');
        return;
      }

      navigate(HomePath);
    },
    [auth, navigate, password, username],
  );

  useEffect(() => {
    if (usernameQueryParam) {
      setUsername(usernameQueryParam);
    }

    if (passwordQueryParam) {
      setPassword(passwordQueryParam);
    }
  }, [passwordQueryParam, usernameQueryParam]);

  useEffect(() => {
    if (
      hasAttemptedQueryParamLogin.current ||
      usernameQueryParam.length === 0 ||
      passwordQueryParam.length === 0
    ) {
      return;
    }

    hasAttemptedQueryParamLogin.current = true;
    void handleLogin(usernameQueryParam, passwordQueryParam);
  }, [handleLogin, passwordQueryParam, usernameQueryParam]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handleLogin();
  };

  return (
    <div className="relative flex min-h-screen items-center justify-start bg-gray-50 py-10 sm:justify-center sm:py-16">
      <form onSubmit={handleSubmit} className="w-full px-4 sm:px-6">
        <div className="relative mx-auto w-full max-w-[520px]">
          <div
            className="mb-6 flex items-center justify-center gap-2.5 sm:absolute sm:bottom-full sm:left-1/2 sm:-translate-x-1/2 sm:whitespace-nowrap"
            aria-label="SimplerSupply"
          >
            <img src="/logo.svg" alt="" className="h-10 w-10" />
            <span className="text-xl font-semibold tracking-tight text-gray-900">
              SimplerSupply
            </span>
          </div>
          <div className="flex w-full flex-col gap-4 rounded-lg border border-gray-100 bg-white p-6 shadow-lg shadow-gray-200/60 sm:gap-6 sm:p-10">
            <h1 className="mb-2 text-center text-2xl font-semibold tracking-tight text-gray-900">
              Sign in to your account
            </h1>
            {error && (
              <div className="rounded border border-red-400 bg-red-100 px-4 py-2 text-red-700">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-600">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                className="mt-1 p-2 w-full border rounded-md"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-600">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="mt-1 p-2 w-full border rounded-md"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
              />
            </div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  className="mr-2"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label htmlFor="rememberMe" className="text-sm text-gray-600">
                  Remember me
                </label>
              </div>
              <div className="text-sm text-blue-500">
                <a href="#">Forgot your password?</a>
              </div>
            </div>
            <Button
              type="submit"
              width="full"
              rounded="full"
              size="lg"
              loading={isLoading}
              loadingIcon={<SpinnerIcon className="w-5 h-5 fill-blue-800" />}
            >
              Sign In
            </Button>

            <div className="text-center">
              <p className="text-sm">
                New here?{' '}
                <Link to={SignupPath} className="text-blue-500">
                  Create an account
                </Link>{' '}
                to get started!
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Login;
